import { Amount } from '@kunalabs-io/kai'
import { min, muldiv } from '@kunalabs-io/kai'
import { VaultInfo } from '@kunalabs-io/kai'
import { PhantomTypeArgument } from '@/shared/gen/_framework/reified'
import { useWalletBalances } from '@/shared/hooks/useWalletBalances'
import { Vault as Vault_ } from '@/shared/gen/kai-sav/vault/structs'
import { TimeLockedBalance } from '@/shared/gen/kai-sav/time-locked-balance/structs'
import { useObject } from '@/shared/hooks/batshitObjectFetcher'
import { useSAVActivityHistory } from 'modules/VaultDetails/hooks/useSAVActivityHistory'
import { Inventory } from '@/shared/position-metrics-alt/inventory'
import Decimal from 'decimal.js'
import { useCurrentWalletAddress } from './useCurrentWalletAddress'

export interface UserVaultInfo {
  equity?: Amount
  unrealizedProfit?: Amount
  realizedProfit?: Amount
  inventoryAtCost?: Amount
  isLoading: boolean
}

function tlbCalcMaxWithdrawable(
  tlb: TimeLockedBalance<PhantomTypeArgument>,
  timestampMs?: number
): bigint {
  const now = timestampMs ? BigInt(timestampMs) / 1000n : tlb.previousUnlockAt

  let unlockableAmount = 0n
  if (tlb.unlockPerSecond > 0 && tlb.unlockStartTsSec < now) {
    const toRemainLocked =
      (tlb.finalUnlockTsSec - min(tlb.finalUnlockTsSec, now)) * tlb.unlockPerSecond
    const lockedAmountRound = (tlb.lockedBalance.value / tlb.unlockPerSecond) * tlb.unlockPerSecond
    unlockableAmount = lockedAmountRound - toRemainLocked
  }

  return tlb.unlockedBalance.value + unlockableAmount
}

function calcTotalAvailableBalance(
  vault: Vault_<PhantomTypeArgument, PhantomTypeArgument>,
  timestampMs?: number
): bigint {
  let total = 0n
  total += vault.freeBalance.value
  total += tlbCalcMaxWithdrawable(vault.timeLockedProfit, timestampMs)

  for (const strategy of vault.strategies.contents) {
    total += strategy.value.borrowed
  }

  return total
}

export const useUserVaultInfo = (
  vault: VaultInfo<PhantomTypeArgument, PhantomTypeArgument>
): UserVaultInfo => {
  const vaultResult = useObject({
    id: vault.id,
    fromBcs: vault.reified.fromBcs,
    options: { refetchInterval: 5000 },
  })
  const vaultData = vaultResult.data

  const walletAddress = useCurrentWalletAddress()
  const walletBalances = useWalletBalances(undefined, walletAddress)

  const ytBalance = walletBalances.isLoading
    ? undefined
    : walletBalances.infos?.get(vault.YT.typeName) || Amount.fromInt(0n, vault.YT.decimals)

  const activityResult = useSAVActivityHistory(vault.id)

  const isLoading = vaultResult.isLoading || walletBalances.isLoading || activityResult.isLoading

  const tvl = vaultData ? calcTotalAvailableBalance(vaultData, Date.now()) : undefined

  let equity: bigint | undefined = undefined
  let ytPrice: number | undefined = undefined
  if (tvl !== undefined && vaultData && ytBalance !== undefined) {
    if (vaultData.lpTreasury.totalSupply.value === 0n) {
      equity = 0n
      ytPrice = undefined
    } else {
      equity = muldiv(ytBalance.int, tvl, vaultData.lpTreasury.totalSupply.value)
      ytPrice = Number(tvl) / Number(vaultData.lpTreasury.totalSupply.value)
    }
  }

  let unrealizedProfit: bigint | undefined = undefined
  let realizedProfit: bigint | undefined = undefined
  let inventoryAtCost: bigint | undefined = undefined
  if (activityResult.data) {
    const inventory = new Inventory()
    for (const activity of activityResult.data) {
      if (BigInt(activity.ytAmount) === 0n) {
        continue
      }

      const price = Number(activity.tValue) / Number(activity.ytAmount)
      if (activity.kind === 'Deposit' || activity.kind === 'Receive') {
        inventory.add('YT', BigInt(activity.ytAmount), new Decimal(price))
      } else if (activity.kind === 'Withdraw' || activity.kind === 'Send') {
        inventory.remove('YT', BigInt(activity.ytAmount), new Decimal(price))
      }
    }

    unrealizedProfit = BigInt(
      inventory.calcUnrealizedPnl(new Map([['YT', new Decimal(ytPrice ?? 0)]])).toFixed(0)
    )
    realizedProfit = BigInt(inventory.getRealizedPnl().toFixed(0))
    inventoryAtCost = equity !== undefined ? equity - unrealizedProfit : 0n
  } else if (activityResult.isLoading === false && activityResult.data === undefined) {
    unrealizedProfit = 0n
    realizedProfit = 0n
    inventoryAtCost = 0n
  }

  const amountOrUndefined = (value: bigint | undefined) =>
    value !== undefined ? Amount.fromInt(value, vault.T.decimals) : undefined

  return {
    equity: amountOrUndefined(equity),
    unrealizedProfit: amountOrUndefined(unrealizedProfit),
    realizedProfit: amountOrUndefined(realizedProfit),
    inventoryAtCost: amountOrUndefined(inventoryAtCost),
    isLoading,
  }
}
